<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Workspaces</h1>
        <p class="console-page__placeholder">
          Browse repository workspaces, filter by source and status, and open a workspace record.
        </p>
        <ConsolePollingStatus
          :active="workspacePollingActive"
          :busy="pollBusy"
          idle-label="Watching workspace status every 3s"
          busy-label="Refreshing workspace status..."
          tone="pending"
        />
      </div>

      <div class="console-page__actions">
        <router-link :to="{ name: 'ConsoleWorkspaceCreate' }" class="button button--primary">
          Create Workspace
        </router-link>
        <button type="button" class="button button--ghost" :disabled="loading" @click="fetchWorkspaces({ showLoading: true })">
          Refresh
        </button>
      </div>
    </div>

    <section class="console-panel">
      <div class="console-filters">
        <label class="console-filter">
          <span class="console-filter__label">Search</span>
          <input
            v-model="searchQuery"
            type="text"
            class="form-input"
            placeholder="Name or description"
          />
        </label>

        <label class="console-filter">
          <span class="console-filter__label">Status</span>
          <select v-model="statusFilter" class="form-input">
            <option value="">Active</option>
            <option value="ready">Ready</option>
            <option value="assembling">Assembling</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="archived">Archived</option>
          </select>
        </label>

        <label class="console-filter">
          <span class="console-filter__label">Source</span>
          <select v-model="sourceFilter" class="form-input">
            <option value="">All</option>
            <option value="github">GitHub</option>
            <option value="upload">Upload</option>
          </select>
        </label>
      </div>

      <ConsoleListState
        :loading="loading"
        :error="error"
        :is-empty="items.length === 0"
        loading-message="Loading workspaces..."
        :empty-message="emptyMessage"
      >
        <div class="console-signal-strip workspace-summary-strip">
          <div class="console-signal console-signal--ready">
            <span class="console-signal__label">Total Workspaces</span>
            <span class="console-signal__value">{{ visibleWorkspaceCount }}</span>
            <span class="console-signal__note">
              Visible across the fleet. Showing {{ items.length }} on this page.
            </span>
          </div>
          <div class="console-signal" :class="activeWorkspaceCount > 0 ? 'console-signal--active' : 'console-signal--pending'">
            <span class="console-signal__label">Active</span>
            <span class="console-signal__value">{{ activeWorkspaceCount }}</span>
            <span class="console-signal__note">Ready or creating across the fleet.</span>
          </div>
          <div class="console-signal" :class="failedWorkspaceCount > 0 ? 'console-signal--failed' : 'console-signal--ready'">
            <span class="console-signal__label">Failed</span>
            <span class="console-signal__value">{{ failedWorkspaceCount }}</span>
            <span class="console-signal__note">Workspaces needing operator attention across the fleet.</span>
          </div>
          <div class="console-signal" :class="githubWorkspaceCount > 0 ? 'console-signal--active' : 'console-signal--pending'">
            <span class="console-signal__label">GitHub Sources</span>
            <span class="console-signal__value">{{ githubWorkspaceCount }}</span>
            <span class="console-signal__note">Visible workspaces backed by GitHub across the fleet.</span>
          </div>
        </div>

        <div class="console-list workspace-list">
          <button
            v-for="workspace in items"
            :key="workspace.workspaceId"
            type="button"
            class="console-card workspace-card"
            @click="openWorkspace(workspace.workspaceId)"
          >
            <div class="console-card__header workspace-card__header">
              <div>
                <h2 class="workspace-card__title">{{ workspace.name || workspace.workspaceId }}</h2>
                <p v-if="workspace.name" class="workspace-card__id">{{ workspace.workspaceId }}</p>
              </div>

              <div class="console-card__meta workspace-card__meta">
                <span class="console-badge workspace-badge" :class="`console-badge--${workspace.status || 'ready'}`">
                  {{ workspace.status || 'ready' }}
                </span>
                <span v-if="workspace.sourceLabel" class="console-badge workspace-badge console-badge--neutral">
                  {{ workspace.sourceLabel }}
                </span>
              </div>
            </div>

            <p v-if="workspace.description" class="workspace-card__description">
              {{ workspace.description }}
            </p>

            <p v-if="workspace.repoUrl" class="workspace-card__repo">
              {{ workspace.repoUrl }}<span v-if="workspace.branch"> @ {{ workspace.branch }}</span>
            </p>

            <div class="console-card__footer workspace-card__footer">
              <span class="workspace-card__summary">
                {{ workspace.jobSummary?.total || 0 }} runs
                <span v-if="workspace.jobSummary?.failed"> - {{ workspace.jobSummary.failed }} failed</span>
              </span>
              <span v-if="workspace.createdAt" class="workspace-card__date">
                <template v-if="workspace.lastSyncedAt">
                  Created {{ formatRelativeTime(workspace.createdAt) }} / Synced {{ formatRelativeTime(workspace.lastSyncedAt) }}
                </template>
                <template v-else>
                  Created {{ formatRelativeTime(workspace.createdAt) }}
                </template>
              </span>
            </div>
          </button>
        </div>

        <div v-if="hasPreviousPage || hasNextPage" class="console-pagination console-pagination--cursor">
          <button
            class="button button--ghost"
            :disabled="!hasPreviousPage"
            @click="goToPreviousPage"
          >
            Previous
          </button>
          <span class="console-pagination__label">Page {{ currentPage }}</span>
          <button
            class="button button--ghost"
            :disabled="!hasNextPage"
            @click="goToNextPage"
          >
            Next
          </button>
        </div>
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import ConsoleListState from '@/components/ConsoleListState.vue'
import ConsolePollingStatus from '@/components/ConsolePollingStatus.vue'
import { formatRelativeTime } from '@/composables/useRelativeTime.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { fetchWorkspaceSummaryPage, fetchWorkspaceCounts, normalizeWorkspaceCounts } from '@/services/workspaceSummary.js'

const router = useRouter()
const { requestJson } = useOrchestratorRequest()
const logger = useFrontendLogger().child('[ConsoleWorkspaces]')
const POLL_INTERVAL_MS = 3000
const MAX_POLL_ERRORS = 5
const TERMINAL_WORKSPACE_STATUSES = new Set(['ready', 'failed', 'archived'])

const loading = ref(true)
const error = ref('')
const items = ref([])
const searchQuery = ref('')
const statusFilter = ref('')
const sourceFilter = ref('')
const currentPage = ref(1)
const pageSize = ref(12)
const pageCursors = ref([undefined])
const hasNextPage = ref(false)
const pollBusy = ref(false)

let filterDebounce = null
let pollTimer = null
let pollErrorCount = 0

const hasPreviousPage = computed(() => currentPage.value > 1)
const workspacePollingActive = computed(() => pollBusy.value || hasPendingWorkspaceItems())
const workspaceCounts = ref(normalizeWorkspaceCounts(undefined))
// Fleet-wide counts (across the whole projection) — these stay correct as the
// user paginates because they are sourced from /workspaces/counts, not from
// the current page items.
const totalWorkspaceCount = computed(() => workspaceCounts.value.total)
const visibleWorkspaceCount = computed(() => workspaceCounts.value.visible)
const activeWorkspaceCount = computed(() =>
  workspaceCounts.value.byStatus.ready + workspaceCounts.value.byStatus.creating,
)
const failedWorkspaceCount = computed(() => workspaceCounts.value.byStatus.failed)
const githubWorkspaceCount = computed(() => workspaceCounts.value.bySource.github)

const emptyMessage = computed(() => {
  if (searchQuery.value || statusFilter.value || sourceFilter.value) {
    return 'No workspaces match the current filters.'
  }
  return 'No workspaces found.'
})

function normalizeWorkspace(item) {
  const workspaceId = item.workspaceId ?? item.id
  const isGithub =
    item.type === 'public-github' || item.type === 'private-github' || item.source === 'github'

  return {
    ...item,
    workspaceId,
    sourceLabel: isGithub ? 'GitHub' : 'Upload',
  }
}

function isPendingWorkspaceStatus(status) {
  return !TERMINAL_WORKSPACE_STATUSES.has(status || 'ready')
}

function hasPendingWorkspaceItems() {
  return items.value.some((item) => isPendingWorkspaceStatus(item.status))
}

function stopWorkspacePolling() {
  if (pollTimer !== null) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
  pollErrorCount = 0
}

function scheduleWorkspacePoll() {
  if (pollTimer !== null || !hasPendingWorkspaceItems()) return

  pollTimer = setTimeout(async () => {
    pollTimer = null
    await pollWorkspaces()
  }, POLL_INTERVAL_MS)
}

async function pollWorkspaces() {
  pollBusy.value = true
  try {
    await fetchWorkspaces({ background: true })
    pollErrorCount = 0

    if (hasPendingWorkspaceItems()) {
      scheduleWorkspacePoll()
    } else {
      stopWorkspacePolling()
    }
  } catch (pollError) {
    pollErrorCount += 1
    logger.warn('Failed to poll workspaces', pollError)

    if (pollErrorCount < MAX_POLL_ERRORS) {
      scheduleWorkspacePoll()
    }
  } finally {
    pollBusy.value = false
  }
}

async function refreshWorkspaceCounts({ background = false } = {}) {
  try {
    workspaceCounts.value = await fetchWorkspaceCounts(requestJson)
  } catch (countsError) {
    if (!background) {
      logger.warn('Failed to fetch workspace counts', countsError)
    }
    // Background failure: keep previous counts; the next successful refresh updates the badges.
  }
}

async function fetchWorkspaces({ showLoading = false, background = false } = {}) {
  if (showLoading) loading.value = true
  if (!background) {
    stopWorkspacePolling()
    error.value = ''
  }

  try {
    const [page] = await Promise.all([
      fetchWorkspaceSummaryPage(requestJson, {
        limit: pageSize.value,
        cursor: pageCursors.value[currentPage.value - 1],
        q: searchQuery.value,
        status: statusFilter.value,
        source: sourceFilter.value,
        includeArchived: false,
      }),
      refreshWorkspaceCounts({ background }),
    ])

    items.value = page.items.map(normalizeWorkspace)
    pageSize.value = page.limit
    hasNextPage.value = page.hasMore

    if (page.hasMore && page.nextCursor) {
      pageCursors.value[currentPage.value] = page.nextCursor
    } else {
      pageCursors.value = pageCursors.value.slice(0, currentPage.value)
    }

    if (hasPendingWorkspaceItems()) {
      scheduleWorkspacePoll()
    } else {
      stopWorkspacePolling()
    }
  } catch (fetchError) {
    if (background) {
      throw fetchError
    }

    items.value = []
    hasNextPage.value = false
    error.value = 'Failed to load workspaces.'
    logger.warn('Failed to fetch workspaces', fetchError)
  } finally {
    if (!background) loading.value = false
  }
}

function resetPagination() {
  currentPage.value = 1
  pageCursors.value = [undefined]
  hasNextPage.value = false
}

function goToPreviousPage() {
  if (!hasPreviousPage.value) return
  currentPage.value -= 1
  fetchWorkspaces({ showLoading: true })
}

function goToNextPage() {
  if (!hasNextPage.value) return
  currentPage.value += 1
  fetchWorkspaces({ showLoading: true })
}

function openWorkspace(workspaceId) {
  router.push({ name: 'ConsoleWorkspaceDetail', params: { id: workspaceId } })
}

watch([searchQuery, statusFilter, sourceFilter], () => {
  resetPagination()
  clearTimeout(filterDebounce)
  filterDebounce = setTimeout(() => {
    fetchWorkspaces({ showLoading: true })
  }, 250)
})

onMounted(() => {
  fetchWorkspaces({ showLoading: true })
})

onUnmounted(() => {
  clearTimeout(filterDebounce)
  stopWorkspacePolling()
})
</script>

<style scoped>
.console-filters {
  grid-template-columns: minmax(0, 1.5fr) repeat(2, minmax(180px, 0.5fr));
}

.workspace-summary-strip {
  margin-bottom: 16px;
}

.workspace-card__title {
  margin: 0;
  font-family: var(--shell-console-font);
  font-size: 1.7rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--shell-text);
}

.workspace-card__id,
.workspace-card__repo,
.workspace-card__summary,
.workspace-card__date,
.workspace-card__description {
  color: var(--shell-text-muted);
}

.workspace-card__id,
.workspace-card__repo,
.workspace-card__summary,
.workspace-card__date {
  font-size: 0.84rem;
}

.workspace-card__id,
.workspace-card__repo {
  margin: 10px 0 0;
}

.workspace-card__repo {
  font-family: "Courier New", Courier, monospace;
}

.workspace-card__description {
  margin: 16px 0 0;
  line-height: 1.7;
}

.workspace-card__footer {
  margin-top: 16px;
  align-items: center;
}

.console-pagination--cursor {
  justify-content: space-between;
}

@media (max-width: 768px) {
  .console-filters {
    grid-template-columns: 1fr;
  }

  .console-pagination--cursor {
    justify-content: center;
  }
}
</style>
